import React, { useEffect, useRef, useState } from 'react';
import {
  Clickable,
  Icon,
  IconButton,
  Image,
} from '@fashionnovateam/nova-ui-toolkit';
import cn from 'classnames';

type ImageFileInputProps = {
  onFileSelect: (file: File) => void;
  label?: string;
  id?: string;
  error: boolean;
  name: string;
  baseFileSrc?: string;
  baseFileFallbackSrc?: string;
};

/**
 * A component to render an image file input with a customizable button and optional error highlighting.
 *
 * @param {Object} props - The properties object for the ImageFileInput component.
 * @param {function} props.onFileSelect - A callback function triggered when a file is selected. It receives the selected file as an argument.
 * @param {string} [props.label] - An optional label for the button that opens the file input dialog.
 * @param {string} [props.id] - The unique identifier for the file input element.
 * @param {boolean} [props.error] - An optional flag indicating whether an error state should be displayed.
 * @param {string} [props.name] - A name to identify elements
 * @param {string} [props.baseFileSrc] - The primary image source URL (typically a thumbnail) displayed when no file has been selected by the user.
 * @param {string} [props.baseFileFallbackSrc] - A fallback image URL (typically the full-resolution photo) used when `baseFileSrc` fails to load. Only applied when the user has not manually selected a file.
 */
export function ImageFileInput({
  onFileSelect,
  label,
  id,
  error,
  name,
  baseFileSrc,
  baseFileFallbackSrc,
}: ImageFileInputProps) {
  // Create a reference to the hidden file input element
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const userSelectedFile = useRef(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(
    baseFileSrc || baseFileFallbackSrc || null,
  );

  useEffect(() => {
    userSelectedFile.current = false;

    if (objectUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(objectUrl);
    }

    setObjectUrl(baseFileSrc || baseFileFallbackSrc || null);
  }, [baseFileSrc, baseFileFallbackSrc]);

  /**
   * A function that triggers a click event on a file input element.
   *
   * This function checks if the `fileInputRef` and its `current` property
   * are defined, and if the `click` method is available. If all conditions
   * are satisfied, it invokes the `click` method on the file input's current DOM node.
   *
   * Typically used to programmatically open a file chooser dialog.
   *
   * @function
   */
  const handleButtonClick = () => {
    if (fileInputRef?.current?.click) fileInputRef.current.click();
  };

  /**
   * Handles the change event for a file input element.
   *
   * This function is triggered when the value of the file input changes.
   * It checks if files are provided and selects the first file from the input.
   * If a file is available, the `onFileSelect` callback is invoked with the selected file,
   * and the state is updated with the selected file.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event triggered by the file input.
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    const f = event.target.files[0];
    if (f) {
      userSelectedFile.current = true;
      onFileSelect(f);
      if (objectUrl?.startsWith('blob:')) URL.revokeObjectURL(objectUrl);
      setObjectUrl(URL.createObjectURL(f));
    }
  };

  /**
   * Revoking the object URL when the component unmounts.
   */
  useEffect(
    () => () => {
      if (objectUrl?.startsWith('blob:')) URL.revokeObjectURL(objectUrl);
    },
    [objectUrl],
  );

  return (
    <>
      {objectUrl ? (
        <Clickable
          data-testid={`image-input-preview-${name}`}
          onClick={handleButtonClick}
          className="w-11 h-11 relative inline-block"
        >
          <Image
            key={objectUrl}
            src={objectUrl || ''}
            fallbackSrc={
              !userSelectedFile.current ? baseFileFallbackSrc : undefined
            }
            alt={label || 'Selected file'}
            className={cn('rounded w-full h-full object-cover', {
              'border-2 border-danger-700': error,
            })}
          />
        </Clickable>
      ) : (
        <IconButton
          data-testid={`image-input-button-${name}`}
          icon={<Icon name="plus/plus-default" size="20px" />}
          label={label || 'Select File'}
          color="secondary"
          onClick={handleButtonClick}
          className={cn({
            'border-2 border-danger-700': error,
          })}
        />
      )}
      <input
        data-testid={`image-input-hidden-input-${name}`}
        type="file"
        ref={fileInputRef}
        id={id}
        accept="image/png, image/jpeg, image/jpg"
        onChange={handleFileChange}
        className="hidden"
        hidden
      />
    </>
  );
}

export default ImageFileInput;
